import { observable } from 'mobx';
import moment from 'moment';
import type { Moment } from '../../lib/moment';
import ElementAttachFile, {
  convertElementAttachFileResponseToArgs,
  type ElementAttachFileResponseArgs
} from '../element_attach_file';

export type UploadFileData = {
  upload_file_name: string;
  content_type: string;
  traveler_information_id: string | null;
  upload_file_base64: string;
  // ElementAttachFileから変換するときだけ下記が必要
  id?: number;
  trip_id?: number;
  element_id?: number;
  file_path?: string;
};

export type WifiElementResponseArgs = {
  id: number;
  summary: string;
  provider?: string;
  provider_text?: string;
  area?: string;
  remark?: string;
  started_at?: string;
  ended_at?: string;
  reservation_number?: string;
  element_attach_files?: ElementAttachFileResponseArgs[];
};

export type WifiElementArgs = {
  id: number;
  summary: string;
  provider?: string;
  providerText?: string;
  providerOptions?: { [key: string]: string };
  area?: string;
  remark?: string;
  startedAt?: Moment;
  endedAt?: Moment;
  reservationNumber?: string;
  attachFiles?: ElementAttachFile[];
};

export const convertWifiElementResponseToArgs = (
  response: WifiElementResponseArgs,
  providerOptions?: { [key: string]: string }
) => {
  const args: WifiElementArgs = {
    id: response.id,
    summary: response.summary,
    provider: response.provider,
    providerText: response.provider_text,
    providerOptions,
    area: response.area,
    remark: response.remark,
    startedAt: response.started_at ? moment(response.started_at) : undefined,
    endedAt: response.ended_at ? moment(response.ended_at) : undefined,
    reservationNumber: response.reservation_number,
    attachFiles: response.element_attach_files?.map(
      file => new ElementAttachFile(convertElementAttachFileResponseToArgs(file))
    )
  };
  return args;
};

export default class WifiElement {
  @observable id: number;
  @observable summary: string;
  @observable provider?: string;
  @observable providerText?: string;
  @observable area?: string;
  @observable remark?: string;
  @observable startedAt?: Moment;
  @observable endedAt?: Moment;
  @observable reservationNumber?: string;
  @observable uploadFileData?: Record<number, Record<string, UploadFileData[]>>;
  @observable attachFiles?: ElementAttachFile[];
  providerOptions?: { [key: string]: string };

  constructor(args: WifiElementArgs) {
    this.id = args.id;
    this.summary = args.summary;
    this.provider = args.provider;
    this.providerOptions = args.providerOptions;
    this.providerText = args.providerText ?? this.resolveProviderText(this.provider);
    this.area = args.area || '';
    this.remark = args.remark || '';
    const defaultStartedAt = args.startedAt ?? moment();
    this.startedAt = defaultStartedAt;
    this.endedAt = args.endedAt ?? defaultStartedAt.clone().add(1, 'hour');
    this.reservationNumber = args.reservationNumber || '';
    this.attachFiles = args.attachFiles || [];
    // 添付済みのファイルを初期セットしないと、編集して送信したときサーバー側で削除扱いになるため
    const initialUploadData = this.buildUploadFileDataFromAttachFiles(this.attachFiles);
    this.uploadFileData = initialUploadData ?? {};

    if ((!this.provider || this.provider === '') && this.providerOptions) {
      const [defaultKey] = Object.keys(this.providerOptions);
      if (defaultKey !== undefined) {
        this.provider = defaultKey;
      }
    }
  }

  get type() {
    return 'wifi';
  }

  get title() {
    return 'WiFi';
  }

  get name() {
    return this.title;
  }

  detail() {
    return '';
  }

  handleUploadFileData(uploadFileData: UploadFileData, wifiElementId: number, travelerId: string | null): void {
    // 出張者が未割り当てのファイル場合、nullにする
    const travelerKey = travelerId ?? 'null';
    const elementId = wifiElementId ?? this.id ?? 0;

    if (!this.uploadFileData) {
      this.uploadFileData = {};
    }
    if (!this.uploadFileData[elementId]) {
      this.uploadFileData[elementId] = {};
    }
    if (!this.uploadFileData[elementId][travelerKey]) {
      this.uploadFileData[elementId][travelerKey] = [];
    }
    this.uploadFileData[elementId][travelerKey].push(uploadFileData);
  }

  handleDeleteFile(wifiElementId: number, travelerId: string | null, index: number): boolean {
    // 出張者が未割り当てのファイル場合、null
    const travelerKey = travelerId ?? 'null';
    const elementId = wifiElementId ?? this.id ?? 0;

    if (
      this.uploadFileData?.[elementId]?.[travelerKey]?.length &&
      this.uploadFileData[elementId][travelerKey].length > index
    ) {
      this.uploadFileData[elementId][travelerKey].splice(index, 1);

      if (this.uploadFileData[elementId][travelerKey].length === 0) {
        delete this.uploadFileData[elementId][travelerKey];
      }

      if (Object.keys(this.uploadFileData[elementId]).length === 0) {
        delete this.uploadFileData[elementId];
      }

      return true;
    }
    return false;
  }

  validationErrors(orderItemStatus?: number): string[] {
    const errors: string[] = [];
    const needStatusGuard = orderItemStatus === 7 || orderItemStatus === 13;

    if (!this.area || this.area === '') {
      errors.push('wifiのエリアを入力してください。');
    }

    if (this.endedAt && this.startedAt && this.endedAt < this.startedAt) {
      errors.push('利用開始以降を指定してください。');
    }

    if (needStatusGuard && (!this.reservationNumber || this.reservationNumber === '')) {
      errors.push('注文番号を入力してください。');
    }

    return errors;
  }

  get description(): string {
    const providerLabel = this.providerLabel;
    const areaLabel = this.area || '';
    const remarkLabel = this.remark || '';
    return `\
<WiFi>
■プロバイダ：${providerLabel}
■ご利用日時：${this.startedAt?.format('MM/DD HH:mm')}〜${this.endedAt?.format('MM/DD HH:mm')}
■ご利用エリア：${areaLabel}
■備考
${remarkLabel}\
`;
  }

  get updateParams() {
    return {
      id: this.id,
      type: this.type,
      provider: this.provider,
      started_at: this.startedAt?.format('YYYY-MM-DD HH:mm:ss'),
      ended_at: this.endedAt?.format('YYYY-MM-DD HH:mm:ss'),
      area: this.area ?? '',
      remark: this.remark ?? '',
      reservation_number: this.reservationNumber,
      element_attach_files:
        this.attachFiles?.map(file => ({
          id: file.id,
          trip_id: file.tripId,
          traveler_information_id: file.travelerInformationId,
          upload_file_name: file.uploadFileName,
          attachable_id: file.attachableId,
          content_type: file.contentType,
          file_path: file.filePath
        })) ?? [],
      upload_file_data: this.uploadFileData ?? {}
    };
  }

  get startTime() {
    return this.startedAt;
  }

  get endTime() {
    return this.endedAt;
  }

  get providerLabel(): string {
    return this.providerText ?? this.resolveProviderText(this.provider) ?? this.provider ?? '';
  }

  // TODO: サーバーサイドと合わせてTransportElementと処理を共用したい
  private buildUploadFileDataFromAttachFiles(
    attachFiles?: ElementAttachFile[]
  ): Record<number, Record<string, UploadFileData[]>> | undefined {
    if (!attachFiles || attachFiles.length === 0) {
      return undefined;
    }
    const data = attachFiles.reduce<Record<number, Record<string, UploadFileData[]>>>((acc, file) => {
      const wifiElementId = file.attachableId;
      if (wifiElementId === undefined || wifiElementId === null) {
        return acc;
      }
      const travelerKey = (file.travelerInformationId ?? 'null').toString();
      acc[wifiElementId] = acc[wifiElementId] ?? {};
      acc[wifiElementId][travelerKey] = acc[wifiElementId][travelerKey] ?? [];
      acc[wifiElementId][travelerKey].push({
        upload_file_name: file.uploadFileName,
        content_type: file.contentType,
        upload_file_base64: '', // 空のときサーバーサイドで既存ファイルとして認識する
        traveler_information_id: file.travelerInformationId,
        id: file.id,
        trip_id: file.tripId,
        element_id: file.attachableId,
        file_path: file.filePath
      });
      return acc;
    }, {});

    return Object.keys(data).length > 0 ? data : undefined;
  }

  private resolveProviderText(provider: string | undefined): string | undefined {
    if (!provider) return undefined;
    return this.providerOptions?.[provider];
  }
}
